// Modified useHandGenerator.js removing default range and ensuring correct range usage
import React from 'react';
import _ from 'lodash';
import { getFilePath } from '../../context/RangeContext/RangeContext';

const { useState, useCallback } = React;

// Constants
const SUITS = ['h', 'd', 'c', 's'];
const VALUES = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'];

// Full 169-hand canonical pool — all pairs, suited, and offsuit combos.
// Hand selection draws from this pool so the full spectrum is always represented,
// not just the hands that appear in the opening range.
const buildFullHandPool = () => {
  const pool = [];
  // Pocket pairs (13)
  for (const rank of VALUES) {
    pool.push({ hand: rank + rank, type: 'pair' });
  }
  // Non-pairs: suited (78) + offsuit (78)
  // VALUES is ordered low→high, so VALUES[i] > VALUES[j] when i > j
  for (let i = 1; i < VALUES.length; i++) {
    for (let j = 0; j < i; j++) {
      const h = VALUES[i] + VALUES[j]; // higher rank first
      pool.push({ hand: h, type: 'suited' });
      pool.push({ hand: h, type: 'offsuit' });
    }
  }
  return pool; // 169 entries
};

const FULL_HAND_POOL = buildFullHandPool();
const CARD_RANKS = {
  'A': 14, 'K': 13, 'Q': 12, 'J': 11, 'T': 10,
  '9': 9, '8': 8, '7': 7, '6': 6, '5': 5,
  '4': 4, '3': 3, '2': 2
};

const BET_TYPES = {
  OPEN: 'OPEN',
  FACING_OPEN_CALL: 'FACING_OPEN_CALL',
  FACING_OPEN_3BET: 'FACING_OPEN_3BET',
  FACING_3BET_CALL: 'FACING_3BET_CALL',
  FACING_3BET_4BET: 'FACING_3BET_4BET'
};

// Helper Functions
const generateDeck = () => {
  const deck = [];
  for (const suit of SUITS) {
    for (const value of VALUES) {
      deck.push({ value, suit });
    }
  }
  return deck;
};

// Improved loadRangeFile function
const loadRangeFile = async (path) => {
  try {
    console.log('Loading range file from path:', path);
    
    // Make sure path is properly formatted
    const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
    const fetchUrl = `/${normalizedPath}`;
    
    console.log('Fetching from URL:', fetchUrl);
    
    // Use fetch with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout
    
    const response = await fetch(fetchUrl, { 
      signal: controller.signal,
      headers: { 'Cache-Control': 'no-cache' }
    });
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      console.error(`Failed to load range file: ${response.status} ${response.statusText}`);
      throw new Error(`Failed to load range file: ${response.status} ${response.statusText}`);
    }
    
    const text = await response.text();
    
    if (!text || text.length < 10) {
      console.error('Range file is empty or too small:', text);
      throw new Error('Range file is empty or too small');
    }
    
    console.log(`Successfully loaded range file (${text.length} bytes)`);
    
    // Log first few lines to debug
    const previewLines = text.split('\n').slice(0, 5);
    console.log('Range file preview:', previewLines);
    
    return text;
  } catch (error) {
    console.error('Error loading range file:', error);
    throw error;
  }
};

// Improved parseRangeFile function
const parseRangeFile = (content) => {
  try {
    const lines = content.trim().split('\n');
    const validHands = new Map();
    
    console.log(`Parsing range file with lines: ${lines.length}`);
    
    // Process header to find correct columns
    const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
    const handIndex = headers.indexOf('hand');
    const typeIndex = headers.indexOf('type');
    const freqIndex = headers.indexOf('frequency');
    
    console.log('Column indices:', { handIndex, typeIndex, freqIndex, headers });
    
    if (handIndex === -1 || freqIndex === -1) {
      throw new Error(`Invalid range file format: missing required headers. Found: ${headers.join(', ')}`);
    }

    // Process range data
    for (let i = 1; i < lines.length; i++) {
      const parts = lines[i].split(',').map(v => v.trim());
      if (parts.length <= Math.max(handIndex, freqIndex)) continue;

      const hand = parts[handIndex];
      const type = typeIndex !== -1 ? parts[typeIndex].toLowerCase() : '';
      
      // Parse frequency with explicit % handling
      const freqStr = parts[freqIndex];
      let frequency = 0;
      
      if (freqStr.includes('%')) {
        // Strip the % sign and parse as integer
        const numStr = freqStr.replace('%', '').trim();
        frequency = parseInt(numStr, 10);
      } else {
        // If no % sign, check if it's a decimal (0-1) or direct percentage (0-100)
        const numVal = parseFloat(freqStr);
        if (!isNaN(numVal)) {
          // If value is between 0-1, assume decimal percentage
          if (numVal >= 0 && numVal <= 1) {
            frequency = Math.round(numVal * 100);
          } else {
            // Otherwise, assume direct percentage value
            frequency = Math.round(numVal);
          }
        }
      }

      // Validate the frequency is within normal range (0-100)
      if (frequency > 100) {
        console.warn(`Unusually high frequency for hand ${hand}: ${frequency}%, normalizing to 100%`);
        frequency = 100;
      }

      if (!hand || frequency <= 0) continue;
      
      // Create standardized hand key (AA, AKs, etc.)
      let standardKey;
      
      // Handle pairs
      if (hand.length === 2 && hand[0] === hand[1]) {
        standardKey = hand; // Just use "AA", "KK", etc.
      }
      // Handle suited/offsuit with explicit type
      else if (type) {
        if (type.includes('pair')) {
          standardKey = hand.substring(0, 2);
        } else if (type.includes('suited')) {
          standardKey = hand.length === 3 ? hand : `${hand}s`;
        } else if (type.includes('offsuit')) {
          standardKey = hand.length === 3 ? hand : `${hand}o`;
        } else {
          standardKey = hand;
        }
      }
      // Try to infer from hand format if type not provided
      else {
        if (hand.length === 3) {
          // Handle hand format like "AKs" or "AKo"
          standardKey = hand;
        } else if (hand.length === 2) {
          // Assume two-card format needs a type suffix
          // Default to offsuit if can't determine
          standardKey = `${hand}o`;
        } else {
          // Unknown format, just use as is
          standardKey = hand;
        }
      }
      
      validHands.set(standardKey, frequency);
    }
    
    console.log(`Parsed ${validHands.size} hands from range file`);
    return validHands;
  } catch (error) {
    console.error('Error parsing range file:', error);
    throw error;
  }
};

// Improved generateHandFromSpec function
const generateHandFromSpec = (hand, type) => {
  console.log('Generating hand from spec:', { hand, type });
  
  // Handle the case where hand is passed as an object with hand and type properties
  if (typeof hand === 'object' && hand !== null && hand.hand) {
    type = hand.type;
    hand = hand.hand;
  }
  
  if (!hand || typeof hand !== 'string' || hand.length < 2) {
    console.error('Invalid hand spec:', { hand, type });
    return null;
  }

  // Extract card values (first 2 characters for most hands)
  const card1Value = hand[0];
  const card2Value = hand.length > 1 ? hand[1] : hand[0];
  
  // Validate card values
  if (!VALUES.includes(card1Value) || !VALUES.includes(card2Value)) {
    console.error('Invalid card values:', { card1Value, card2Value });
    return null;
  }

  // Determine if suited based on the type parameter or hand format
  let suited = false;
  if (typeof type === 'string') {
    suited = type.toLowerCase() === 'suited' || type.toLowerCase() === 's';
  } else if (hand.length > 2) {
    suited = hand[2].toLowerCase() === 's';
  }

  if (card1Value === card2Value) {
    // Pocket pair
    const availableSuits = _.shuffle(SUITS);
    return {
      card1Value: card1Value,
      card1Suit: availableSuits[0],
      card2Value: card2Value,
      card2Suit: availableSuits[1]
    };
  }
  
  if (suited) {
    const suit = _.sample(SUITS);
    return {
      card1Value: card1Value,
      card1Suit: suit,
      card2Value: card2Value,
      card2Suit: suit
    };
  } else {
    // Ensure different suits for offsuit hands
    const suits = _.shuffle(SUITS);
    return {
      card1Value: card1Value,
      card1Suit: suits[0],
      card2Value: card2Value,
      card2Suit: suits[1]
    };
  }
};

// Helper function to format hand for range lookup
const formatHandForLookup = (hand, type) => {
  return hand + type.charAt(0).toLowerCase();
};

const orderCardsByRank = (card1, card2) => {
  const rank1 = CARD_RANKS[card1.value];
  const rank2 = CARD_RANKS[card2.value];
  
  if (rank1 >= rank2) {
    return {
      card1Value: card1.value,
      card1Suit: card1.suit,
      card2Value: card2.value,
      card2Suit: card2.suit
    };
  } else {
    return {
      card1Value: card2.value,
      card1Suit: card2.suit,
      card2Value: card1.value,
      card2Suit: card1.suit
    };
  }
};

// Helper to convert any range path to hero's open range path based on file structure
const getOpenRangePath = (rangePath, heroPosition) => {
  if (!rangePath || !heroPosition) return rangePath;

  console.log(`Transforming range path to opening range for ${heroPosition}. Original: ${rangePath}`);
  
  // Normalize the position to lowercase
  const position = heroPosition.toLowerCase();
  
  // Extract the base path (everything up to the ranges directory)
  const pathParts = rangePath.split('/ranges/');
  if (pathParts.length !== 2) {
    console.log(`Unable to parse range path: ${rangePath}`);
    return rangePath;
  }
  
  const basePath = pathParts[0] + '/ranges/';
  
  // Construct the opening range path
  // Format is /databases/preflop/ranges/{position}/{position}_open.csv
  const openRangePath = `${basePath}${position}/${position}_open.csv`;
  
  console.log(`Transformed to opening range path: ${openRangePath}`);
  return openRangePath;
};

// Bet types where hero has already opened earlier in the hand, so their hole
// cards are constrained to whatever they'd actually open with — not the full
// deck. Sampling pool for these is hero's own opening range for heroPosition.
// (Only FACING_3BET_CALL / FACING_3BET_4BET are reachable from the quiz
// config today; FACING_4BET_CALL/SQUEEZE_4BET/CALL_VS_3BET aren't wired up to
// a selectable scenario yet, so they're left on the full-deck fallback rather
// than guessing which prior-action range file they'd need.)
const RANGE_CONSTRAINED_BET_TYPES = new Set(['FACING_3BET_CALL', 'FACING_3BET_4BET']);

// Simple in-memory cache so we don't refetch the same range CSV every hand.
const rangePoolCache = new Map();

// Loads a range CSV and turns it into a sampling pool: one entry per hand
// class actually in the range (frequency > 0), e.g. { hand: 'A5s', frequency: 50 }.
const loadRangePool = async (path) => {
  if (rangePoolCache.has(path)) return rangePoolCache.get(path);
  const content = await loadRangeFile(path);
  const rangeMap = parseRangeFile(content);
  const pool = [];
  for (const [hand, frequency] of rangeMap.entries()) {
    if (frequency > 0) pool.push({ hand, frequency });
  }
  rangePoolCache.set(path, pool);
  return pool;
};

// Weighted random pick — a hand hero opens 100% of the time should come up
// about twice as often as one they only open 50% of the time.
const weightedSample = (pool) => {
  if (!pool || pool.length === 0) return null;
  const total = pool.reduce((sum, p) => sum + (p.frequency || 1), 0);
  let r = Math.random() * total;
  for (const p of pool) {
    r -= (p.frequency || 1);
    if (r <= 0) return p;
  }
  return pool[pool.length - 1];
};

export const useHandGenerator = () => {
  const [currentHand, setCurrentHand] = useState(null);
  const [currentPosition, setCurrentPosition] = useState(null);

  // Helper to get the dedup key from a hand spec string ("AKs" → "AK", "AA" → "AA")
  const getHandKey = (handSpec) => {
    if (!handSpec || handSpec.length < 2) return handSpec;
    return handSpec[0] + handSpec[1];
  };

  // Filter the pool by usedHandKeys (rank-pair level).
  // Falls back to the full pool if everything has been seen.
  const filterUnusedHands = (pool, usedHandKeys) => {
    if (!usedHandKeys || usedHandKeys.size === 0) return pool;
    const filtered = pool.filter(h => {
      const key = getHandKey(typeof h === 'object' ? h.hand : h);
      return !usedHandKeys.has(key);
    });
    return filtered.length > 0 ? filtered : pool;
  };

  // generateRandomFromDeck — deals 2 cards from a shuffled 52-card deck.
  // This produces the correct combo distribution:
  //   offsuit hands: 12 combos each (~71% of all deals)
  //   suited hands:  4 combos each  (~24%)
  //   pairs:         6 combos each  (~6%)
  // Previously we sampled uniformly from 169 hand categories, which
  // over-represented pairs and suited hands vs. true random.
  const generateRandomFromDeck = useCallback((usedHandKeys = new Set()) => {
    const deck = generateDeck();
    const MAX_ATTEMPTS = 200;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      const shuffled = _.shuffle(deck);
      const c1 = shuffled[0];
      const c2 = shuffled[1];
      const ordered = orderCardsByRank(c1, c2);
      const key = ordered.card1Value + ordered.card2Value;

      if (usedHandKeys.size === 0 || !usedHandKeys.has(key)) {
        return ordered;
      }
    }

    // Fallback if all rank combos exhausted (shouldn't happen in normal play)
    const shuffled = _.shuffle(deck);
    return orderCardsByRank(shuffled[0], shuffled[1]);
  }, []);

  // generateHand:
  //  - OPEN / FACING_OPEN_CALL / FACING_OPEN_3BET / LIMP: hero hasn't acted
  //    yet, so deal from a true random deck — full spectrum at true frequency.
  //  - FACING_3BET_CALL / FACING_3BET_4BET: hero already opened, so their
  //    hand is constrained to hero's own opening range for heroPosition —
  //    sample from that range file instead of the full deck.
  //  - forcedHandClass: when a wrong answer's hand is due to come back
  //    (see GameContext hand tracking), deal that exact hand class instead
  //    of drawing at random, so it's a guaranteed return, not just a chance.
  const generateHand = useCallback(async (betType, range, heroPosition, openPosition, usedHandKeys = new Set(), forcedHandClass = null) => {
    console.log('Generating hand:', { betType, range, heroPosition, openPosition, usedCount: usedHandKeys.size, forcedHandClass });

    try {
      if (forcedHandClass) {
        const forced = generateHandFromSpec(forcedHandClass);
        if (forced) return { hand: forced };
      }

      if (RANGE_CONSTRAINED_BET_TYPES.has(betType)) {
        const openRangePath = getOpenRangePath(range, heroPosition);
        try {
          const pool = await loadRangePool(openRangePath);
          const eligiblePool = filterUnusedHands(pool, usedHandKeys);
          const picked = weightedSample(eligiblePool);
          if (picked) {
            const dealt = generateHandFromSpec(picked.hand);
            if (dealt) return { hand: dealt };
          }
        } catch (rangeError) {
          console.error(`Could not load opening range (${openRangePath}) for ${betType}, falling back to full deck:`, rangeError);
        }
      }

      const hand = generateRandomFromDeck(usedHandKeys);
      return { hand };
    } catch (error) {
      console.error('Error generating hand:', error);
      const deck = generateDeck();
      const shuffled = _.shuffle(deck);
      return { hand: orderCardsByRank(shuffled[0], shuffled[1]) };
    }
  }, [generateRandomFromDeck]);

  return {
    currentHand,
    currentPosition,
    generateHand
  };
};

export default useHandGenerator;